/**
 * The SPA's auth context (epic 076 task 03): loads `GET /admin/auth/me` on
 * mount, exposes the session user and the three auth actions. The session
 * lives in the api's HttpOnly cookie — nothing user-identifying is stored
 * outside React state, and no credential ever touches localStorage.
 *
 * All four endpoint calls go through the shared `apiFetch` wrapper with
 * `skipAuthRedirect`: a 401 here means "not signed in" (the provider's own
 * state answer), "current password incorrect" (change-password — the body
 * message discriminates, see {@link INVALID_CREDENTIALS_MESSAGE}), or a
 * logout whose server-side revocation failed (the outcome is reported, not
 * assumed) — never a reason to bounce the browser, and the wrapper's
 * redirect would loop on /login (r1 H-3). The URLs are absolute off
 * `API_BASE_URL` — same resolution as every per-screen client: the dev
 * topology serves the SPA on :5173 against the api on :3001 with no vite
 * proxy, so a relative /admin/auth/* would hit the vite dev server, not
 * the api.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError, apiFetch } from './api';

/** The api's absolute base (the SPA's origin differs from it in dev). */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * The api's wrong-current-password 401 body message — the exact wire string
 * of `INVALID_CREDENTIALS` in `api/src/modules/admin/auth/auth.service.ts`.
 * The api discriminates change-password's two 401s only by body: a wrong
 * current password carries this message, while a session failure (the
 * guard's bare `UnauthorizedException`) carries Nest's default
 * 'Unauthorized'. Callers treat equality with this string as the one honest
 * basis for a wrong-password claim; any other 401 body is a session
 * failure (QA round 18 #139 ride-along).
 */
export const INVALID_CREDENTIALS_MESSAGE = 'invalid credentials';

/** The two roles on `admin_user` (epic 103 D1) — mirrors the api's wire union. */
export type AdminUserRole = 'admin' | 'employee';

/** The authenticated admin user as the api's login/me payload returns it. */
export interface AdminAuthUser {
  id: string;
  email: string;
  displayName: string;
  role: AdminUserRole;
  mustChangePassword: boolean;
}

interface AuthContextValue {
  user: AdminAuthUser | null;
  /** True while the mount-time `me` load is in flight. */
  loading: boolean;
  /** Verify the credentials, mint the session cookie, adopt the user. */
  login(email: string, password: string): Promise<AdminAuthUser>;
  /**
   * Revoke the session api-side, then clear the local user either way
   * (never throws). The outcome states honestly whether the api confirmed
   * the revocation — a failed call must not read as a clean sign-out.
   */
  logout(): Promise<LogoutOutcome>;
  /** Re-read `me` — used after a forced password change clears the flag. */
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The sign-out outcome: `revoked` is true only when the api confirmed the
 * revocation (204). A 401 (the server did not recognize the session — the
 * #139 stand symptom) and a network failure both answer false: the local
 * session still clears, but no screen may claim a clean sign-out.
 */
export interface LogoutOutcome {
  revoked: boolean;
}

/** Map a failed auth response to the shared `ApiError` (Nest body or statusText). */
async function apiErrorFrom(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => ({}));
  const message =
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof body.message === 'string' &&
    body.message.length > 0
      ? body.message
      : response.statusText;
  return new ApiError(response.status, message);
}

/** One auth-endpoint call returning the user envelope (`{ data: user }`). */
async function authUserRequest(
  path: string,
  init: { method: 'GET' | 'POST'; body?: string }
): Promise<AdminAuthUser> {
  const response = await apiFetch(`${API_BASE_URL}/admin/auth${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json' },
    skipAuthRedirect: true,
  });
  if (!response.ok) {
    throw await apiErrorFrom(response);
  }
  const envelope = (await response.json()) as { data: AdminAuthUser };
  return envelope.data;
}

/**
 * POST /admin/auth/change-password — current + new. Opted out of the 401
 * redirect on purpose: the endpoint answers TWO different 401s, and bouncing
 * to /login on the wrong-password one would lose the form. The body message
 * discriminates them — {@link INVALID_CREDENTIALS_MESSAGE} is a wrong
 * current password with the session alive; anything else is the guard
 * rejecting the session itself. Callers branch on that.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const response = await apiFetch(`${API_BASE_URL}/admin/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
    skipAuthRedirect: true,
  });
  if (!response.ok) {
    throw await apiErrorFrom(response);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminAuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setUser(await authUserRequest('/me', { method: 'GET' }));
    } catch {
      // No session (or the api is unreachable) — the SPA renders signed out.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string): Promise<AdminAuthUser> => {
    const authenticated = await authUserRequest('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(authenticated);
    return authenticated;
  }, []);

  const logout = useCallback(async (): Promise<LogoutOutcome> => {
    let revoked = false;
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/auth/logout`, {
        method: 'POST',
        skipAuthRedirect: true,
      });
      revoked = response.ok;
    } catch {
      // The api is unreachable — the session row may still be live
      // server-side. Either way the local session clears below; a failed
      // logout must not trap the user, but the outcome says so honestly.
    } finally {
      setUser(null);
    }
    return { revoked };
  }, []);

  /**
   * The forced first-time password change (ADR-019 item 5): while the flag is
   * set, every path but the change-password screen itself redirects away —
   * cleared by the flag going false, which the change-password screen's
   * refresh() produces.
   */
  if (user?.mustChangePassword === true && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
